/**
 * 状态 / 分组 chip —— 控制台设计语言里最小的状态单元。
 *
 * 三条纪律（这就是它从 ConsolePage 里被抽出来的原因）：
 *  1. 只读语义 token。颜色一律 `var(--...)`，**不允许出现十六进制**：
 *     原先 ConsolePage 把 info/magic 写死成 #eaf4ff / #005bd3 / #f1edff / #5b34ea，
 *     换主题或换方向时这两档不会跟着变，属于「第二套色板」。
 *  2. 文字色用 `*-text` 档而不是填充档：`--success-text` / `--warning-text` /
 *     `--danger-text` / `--info-text` 才是与 `*-subtle` 背景成对接受过
 *     WCAG 4.5:1 审计的前景色（见 styles/contrast.ts 的 CONTRAST_PAIRS）。
 *  3. 逻辑与渲染分离：`chipTone()` 是纯函数，可以在 node 环境直接断言
 *     「没有硬编码颜色」，不需要 jsdom。
 */

import type { ReactElement, ReactNode } from "react";

export type ChipTone = "neutral" | "success" | "warning" | "critical" | "info" | "magic";

export interface ChipToneStyle {
  readonly background: string;
  readonly color: string;
}

/** 语义档 → CSS 变量。键必须覆盖 ChipTone 的每一档（用 Record 让 TS 兜住漏配）。 */
const TONE_BACKGROUND: Readonly<Record<ChipTone, string>> = {
  neutral: "var(--bg-sunken)",
  success: "var(--success-subtle)",
  warning: "var(--warning-subtle)",
  critical: "var(--danger-subtle)",
  info: "var(--info-subtle)",
  magic: "var(--magic-subtle)",
};

const TONE_FOREGROUND: Readonly<Record<ChipTone, string>> = {
  neutral: "var(--fg-secondary)",
  success: "var(--success-text)",
  warning: "var(--warning-text)",
  critical: "var(--danger-text)",
  info: "var(--info-text)",
  magic: "var(--magic-text)",
};

/**
 * chip 的配色。`active` 时反色成主操作色（控制台的国家组选中态就是这么用的）。
 * 纯函数：不读 DOM、不读主题，测试可以直接断言返回值。
 */
export function chipTone(tone: ChipTone, active = false): ChipToneStyle {
  if (active) return { background: "var(--accent)", color: "var(--accent-ink)" };
  return { background: TONE_BACKGROUND[tone], color: TONE_FOREGROUND[tone] };
}

export interface ChipProps {
  readonly children: ReactNode;
  readonly tone?: ChipTone;
  readonly active?: boolean;
}

export function Chip({ children, tone = "neutral", active = false }: ChipProps): ReactElement {
  const style = chipTone(tone, active);
  return (
    <span
      className="inline-flex items-center gap-1 whitespace-nowrap px-2 text-label font-medium"
      style={{
        height: 20, // 控制台定稿：chip 固定 20px，不参与行高计算
        borderRadius: "var(--radius-md)",
        background: style.background,
        color: style.color,
      }}
    >
      {children}
    </span>
  );
}
